import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { type ResourceTypeText } from '@common/types/resource';
import { Scale } from '@components/ui/Scale/Scale';
import cn from 'classnames';

import { appleDevices } from '@/common/app/device';
import { convertRemToPixels } from '@/common/lib/convertRemtoPx';
import { ClaimAnimationContext } from '@/context/ClaimAnimationContext';

import { Stock } from './Stock/Stock';
import { StockAnimation } from './Stock/StockAnimation';

import css from './styles.module.scss';

interface ResourceProgressProps {
  resourceKind: ResourceTypeText;
  production: number;
  stock: number;
  capacity: number;
  updatedAt: string;
  localStockUpdatedAt?: string;
  plantId?: string;
}

const ResourceProgress = forwardRef<HTMLDivElement, ResourceProgressProps>(
  (
    { production, updatedAt, stock, capacity, plantId, localStockUpdatedAt },
    ref,
  ) => {
    const {
      isAnimationStart,
      isStockAnimationStart,
      setIsStockAnimationStart,
    } = useContext(ClaimAnimationContext);
    const [animationValues, setAnimationValue] = useState({
      first: 0,
      second: 0,
      third: 0,
      fourth: 0,
    });
    const countUpRef = useRef<HTMLDivElement | null>(null);
    const prevStock = useRef<number>(0);

    useEffect(() => {
      if (isAnimationStart && stock === 0 && prevStock.current) {
        const first = prevStock.current;

        setAnimationValue({
          first,
          second: Math.round(first * 0.625),
          third: Math.round(first * 0.25),
          fourth: 0,
        });
        setIsStockAnimationStart(true);
      }

      return (): void => {
        const animateValue = countUpRef.current?.textContent;

        if (animateValue) {
          prevStock.current = Number(animateValue);
        }
      };
    }, [stock, isAnimationStart]);

    return (
      <div ref={ref} className="w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <span
            className={cn(
              css.stock,
              !appleDevices.includes(Telegram.WebApp.platform) &&
                css.textGradient,
            )}
          >
            <Stock
              ref={countUpRef}
              stock={stock}
              limit={capacity}
              productionRate={production}
              updatedAt={updatedAt}
              localStockUpdatedAt={localStockUpdatedAt}
              withoutFormat={true}
              plantId={plantId}
            />
            {isStockAnimationStart && (
              <StockAnimation animationValues={animationValues} />
            )}
          </span>
          <div className="w-20">
            <Scale
              height={convertRemToPixels(0.5)}
              percentage={capacity ? (stock * 100) / capacity : 0}
            />
          </div>
        </div>
      </div>
    );
  },
);

export { ResourceProgress };
